// Single place that turns a Date into a time / date+time string per the
// user's clock-display preferences. Every surface that renders a time
// should route through this so they stay in sync with the prefs.
//
// Defaults — used until settings load — keep the historical look of the
// app: 24h, no seconds.

export type TimeFormat = '12h' | '24h';

export interface TimePrefs {
  timeFormat: TimeFormat;
  showSeconds: boolean;
}

export const DEFAULT_TIME_PREFS: TimePrefs = {
  timeFormat: '24h',
  showSeconds: false,
};

export function formatTime(d: Date, prefs: TimePrefs = DEFAULT_TIME_PREFS): string {
  return d.toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    ...(prefs.showSeconds ? { second: '2-digit' } : {}),
    hour12: prefs.timeFormat === '12h',
  });
}

// Short date in the user's locale ("May 21" / "Jan 4"). Year is dropped
// for current-year dates and shown for cross-year clarity.
export function formatDateShort(d: Date): string {
  const sameYear = d.getFullYear() === new Date().getFullYear();
  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' }),
  });
}

// "Smart" date+time. Used wherever a single line needs to convey both a
// date and a time. The date portion adapts to context:
//   - same calendar day  → "14:23"             (date implied)
//   - tomorrow / yesterday → "Tomorrow · 14:23"
//   - any other day      → "May 21 · 14:23"
//
// Pass `withDate: 'always'` to force the date through (e.g. inside the
// missed-tasks banner where every row is in the past and the date is
// load-bearing).
export function formatSmartDateTime(
  d: Date,
  prefs: TimePrefs = DEFAULT_TIME_PREFS,
  options: { withDate?: 'auto' | 'always' } = {},
): string {
  const withDate = options.withDate ?? 'auto';
  const time = formatTime(d, prefs);

  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  if (withDate === 'auto' && sameDay) return time;

  const startOf = (x: Date) => {
    const c = new Date(x);
    c.setHours(0, 0, 0, 0);
    return c;
  };
  const diffDays = Math.round(
    (startOf(d).getTime() - startOf(now).getTime()) / 86_400_000,
  );
  let datePart: string;
  if (diffDays === 0) datePart = 'Today';
  else if (diffDays === 1) datePart = 'Tomorrow';
  else if (diffDays === -1) datePart = 'Yesterday';
  else datePart = formatDateShort(d);

  return `${datePart} · ${time}`;
}
